import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useLang } from "./LanguageContext";

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/site/content");
      setContent(r.data || {});
    } catch (e) {
      setContent({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, loading, refresh, setContent }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);

// Helper hook: returns a getter c("hero.subtitle") that resolves to current language automatically
export const useContentText = () => {
  const { content } = useContent();
  const { lang } = useLang();

  const c = (path, fallback = "") => {
    if (!content) return fallback;
    const parts = path.split(".");
    let cur = content;
    for (const p of parts) {
      if (cur == null) return fallback;
      cur = cur[p];
    }
    if (cur == null) {
      // Try language suffix variant: e.g. "hero.subtitle" -> "hero.subtitle_es"
      let cur2 = content;
      const last = parts[parts.length - 1];
      const head = parts.slice(0, -1);
      for (const p of head) {
        if (cur2 == null) return fallback;
        cur2 = cur2[p];
      }
      if (cur2 && cur2[`${last}_${lang}`] != null) return cur2[`${last}_${lang}`];
      return fallback;
    }
    if (typeof cur === "object" && cur !== null) {
      if (cur[lang] != null) return cur[lang];
    }
    return cur;
  };

  // Localised getter: c("hero", "subtitle") tries field_<lang>, then field
  const cl = (section, field, fallback = "") => {
    const sec = content?.[section];
    if (!sec) return fallback;
    if (sec[`${field}_${lang}`] != null) return sec[`${field}_${lang}`];
    if (sec[field] != null) return sec[field];
    return fallback;
  };

  return { content, c, cl, lang };
};
